import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import deliveryService, { DeliveryConfig } from '../services/deliveryService';
import restaurantService from '../services/restaurantService';
import { PlanFeaturesPayload } from '../utils/planFeatures';
import {
  getStoredGuestOrderType,
  parseGuestOrderType,
  resolveGuestOrderType,
  saveGuestOrderType,
} from '../utils/guestOrderTypeStorage';

/** Resolves guest cart/menu order type from URL, session storage, then restaurant plan features (1 dine-in, 2 delivery, 3 takeaway). */
export function useGuestOrderType(appName?: string): number {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const tableQr = Boolean(searchParams.get('token'));
  const [orderType, setOrderType] = useState(() => (tableQr ? 1 : getStoredGuestOrderType(appName) ?? 1));

  useEffect(() => {
    if (!appName) {
      return;
    }
    const urlType = tableQr ? 1 : parseGuestOrderType(typeParam);
    if (urlType != null) {
      saveGuestOrderType(appName, urlType);
    }
    let cancelled = false;
    Promise.all([
      restaurantService.getRestaurant(appName),
      deliveryService.getConfig(appName).catch(() => null as DeliveryConfig | null),
    ])
      .then(([res, config]) => {
        if (cancelled) {
          return;
        }
        const features: PlanFeaturesPayload | undefined = res.features;
        const next = resolveGuestOrderType({
          appName,
          requested: urlType,
          tableQr,
          features,
          deliveryConfig: config,
        });
        setOrderType(next);
        const stored = getStoredGuestOrderType(appName);
        if (urlType != null || tableQr || stored == null || next === stored) {
          saveGuestOrderType(appName, next);
        }
      })
      .catch(() => {
        const stored = tableQr ? 1 : getStoredGuestOrderType(appName) ?? urlType;
        if (stored != null) {
          setOrderType(stored);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [appName, typeParam, tableQr]);

  return orderType;
}
